// TRACKED KIT EDIT (Oleg @ IAKOE, 2026-10-01): trackpad swipe / horizontal wheel moves one
// slide and rows that can move get is-draggable (grab cursor in ia-slider.css), ported from
// kit 2.2.0 (same change in the kit repo). Snippet param `wheel: false` opts out. Re-diff on
// `iakoe upgrade`: the upgraded kit file carries it, this note then goes.
import KeenSlider from 'ia-keen-slider';

if (!customElements.get('ia-slider')) {

  class IASlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.currentSlide = 0;
      this.currentSlideElement = null;
      this.prevButton = null;
      this.nextButton = null;
      this.progressFill = null;
      this.bulletElements = null;
      this.autoplay = this.getAttribute('data-auto-play') === 'true';
      this.autoplayDelay = parseInt(this.getAttribute('data-auto-play-delay') || '2000', 10);
      this.autoplayTimeout = null;
      this.mouseOver = false;
      this.loop = this.dataset.loop === 'true';
      this.vertical = this.dataset.vertical === 'true';

      // console.log('IASlider constructor', this);
    }
  
    connectedCallback() {
      // Defer initialization if data-defer-init is set
      if (this.dataset.deferInit === 'true') {
        return;
      }
      this.initializeSlideshow();
    }
  
    disconnectedCallback() {
      this.clearAutoplayTimeout();
      if (this.slider) {
        this.slider.destroy();
      }
    }
  
    initializeSlideshow() {
      const carousel = this.querySelector('.ia-slider__slides');
      this.currentSlideElement = this.querySelector('.js-ia-slider-current');
      this.prevButton = this.querySelector('.js-ia-slider-prev');
      this.nextButton = this.querySelector('.js-ia-slider-next');
      this.progressFill = this.querySelector('.ia-slider__progress-fill');
      this.bulletElements = this.querySelectorAll('[data-slide-index], .ia-slider__bullet, .js-ia-slider-bullet');
  
      if (!carousel) return;

      const sliderConfig = {
        loop: this.loop,
        vertical: this.vertical,
        slides: {
          perView: this.style.getPropertyValue('--items-desktop'),
          spacing: this.style.getPropertyValue('--spacing-desktop').replace('px', '')
        },
        breakpoints: {
          '(max-width: 1023px)': {
            slides: {
              perView: this.style.getPropertyValue('--items-mobile'),
              spacing: this.style.getPropertyValue('--spacing-mobile').replace('px', ''),
              origin: this.dataset.centeredMobile === 'true' ? 0.25 : undefined,
            },
          },
          '(min-width: 1600px)': {
            slides: {
              spacing: this.style.getPropertyValue('--spacing-desktop').replace('px', ''),
              perView: this.style.getPropertyValue('--items-desktop-large') || this.style.getPropertyValue('--items-desktop'),
            },
          },
        },
        created: (slider) => {
          this.classList.add('is-initialized');
          const firstSlide = this.querySelector('.ia-slider__slide');
          if (firstSlide) {
            firstSlide.classList.add('is-active');
          }
          this.createProgressBar();
          this.initializeBullets();
          this.updateBullets();
          
          // Update progress after slider is created
          setTimeout(() => {
            this.updateProgress();
          }, 50);
        },
        slideChanged: (slider) => {
          this.updateProgress();
        }
      };

      // Enable loop for autoplay
      if (this.autoplay) {
        sliderConfig.loop = true;
      }

      // Initialize slider with autoplay plugin if enabled
      const plugins = this.autoplay ? [this.createAutoplayPlugin()] : [];
      this.slider = new KeenSlider(carousel, sliderConfig, plugins)

      if (this.dataset.wheel !== 'false' && !this._wheelBound) {
        this._wheelBound = true;
        this.bindWheel();
      }
      this.updateDraggable();
      this.slider.on('updated', () => this.updateDraggable());
      this.slider.on('optionsChanged', () => this.updateDraggable());
  
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.slider.prev();
        });
      }
  
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.slider.next();
        });
      }
  
      this.slider.on('slideChanged', (slide) => {
        const allSlides = this.querySelectorAll('.ia-slider__slide');
        allSlides.forEach(slide => slide.classList.remove('is-active'));
        
        const currentSlide = this.slider.track.details.rel;
        if (allSlides[currentSlide]) {
          allSlides[currentSlide].classList.add('is-active');
        }
        
        if (this.slider?.track?.details?.rel >= 0 && this.currentSlideElement) {
          this.currentSlideElement.textContent = this.slider.track.details.rel + 1;
        }
        
        this.updateBullets();
        this.updateProgress();
      });

    }
  
    // Whether the row can move at all: a loop, or slides past the view. keen drags in both
    // cases; with nothing to scroll the trackpad and the grab cursor stay off.
    canMove() {
      const d = this.slider?.track?.details;
      if (!d) return false;
      if (this.slider.options.loop) return d.slides.length > 1;
      return d.maxIdx > d.minIdx;
    }

    // is-draggable carries the grab cursor (ia-slider.css), so a row that moves says so.
    updateDraggable() {
      this.classList.toggle('is-draggable', this.canMove());
    }

    // keen moves on a pressed pointer only, so a Mac trackpad's two-finger swipe (a
    // horizontal wheel event) did nothing and a scrolling row read as static. A horizontal
    // swipe (more deltaX than deltaY) moves one slide per gesture. The gesture ends when its
    // events stop for 200ms, OR when a new swipe starts inside the previous one's inertia:
    // inertia only fades, so the speed rising again after it began to fade, or a turn the
    // other way, is a new swipe (without that, quick swipes in a row were swallowed). A
    // vertical scroll passes through to the page, and so does everything when the row has
    // nothing to scroll.
    bindWheel() {
      let travel = 0;
      let moved = false;
      let peak = 0;
      let last = 0;
      let fading = false;
      let idle = null;
      const reset = () => {
        travel = 0;
        moved = false;
        peak = 0;
        last = 0;
        fading = false;
      };
      this.addEventListener('wheel', (event) => {
        if (!this.slider || this.vertical || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        if (!this.canMove()) return;
        event.preventDefault();
        const speed = Math.abs(event.deltaX);
        if (moved) {
          const turned = Math.sign(event.deltaX) !== Math.sign(travel) && speed > 6;
          const rising = fading && speed > last * 1.5 + 3;
          if (turned || rising) reset();
        }
        if (speed > peak) peak = speed;
        if (speed < peak * 0.7) fading = true;
        last = speed;
        clearTimeout(idle);
        idle = setTimeout(reset, 200);
        if (moved) return;
        travel += event.deltaX;
        if (Math.abs(travel) < 30) return;
        moved = true;
        if (travel > 0) {
          this.slider.next();
        } else {
          this.slider.prev();
        }
      }, { passive: false });
    }

    initializeBullets() {
      if (!this.bulletElements || this.bulletElements.length === 0) {
        return;
      }

      this.bulletElements.forEach((bullet) => {
        const slideIndex = bullet.getAttribute('data-slide-index');
        if (slideIndex !== null) {
          bullet.addEventListener('click', () => {
            this.goToSlide(parseInt(slideIndex, 10));
          });
        }
      });
    }

    updateBullets() {
      if (!this.bulletElements || this.bulletElements.length === 0 || !this.slider) {
        return;
      }

      const currentSlide = this.slider.track.details.rel;
      
      this.bulletElements.forEach((bullet, index) => {
        const slideIndex = bullet.getAttribute('data-slide-index');
        const targetIndex = slideIndex !== null ? parseInt(slideIndex, 10) : index;
        
        if (targetIndex === currentSlide) {
          bullet.classList.add('is-active');
          bullet.setAttribute('aria-current', 'true');
        } else {
          bullet.classList.remove('is-active');
          bullet.setAttribute('aria-current', 'false');
        }
      });
    }
  
    createProgressBar() {
      if (this.dataset.showProgress !== 'true') return;

      if (this.querySelector('.ia-slider__progress')) return;

      const progressContainer = document.createElement('div');
      progressContainer.className = 'ia-slider__progress ' + this.dataset.colorSchemeUi;

      const progressBar = document.createElement('div');
      progressBar.className = 'ia-slider__progress-bar';

      const progressFill = document.createElement('div');
      progressFill.className = 'ia-slider__progress-fill';

      progressBar.appendChild(progressFill);
      progressContainer.appendChild(progressBar);
      this.appendChild(progressContainer);

      this.progressFill = progressFill;
    }

    updateProgress() {
      const d = this.slider.track.details;
      const totalSlides = d?.slides?.length || 0;
      const currentSlide = d.rel;
      this.currentSlide = currentSlide;
      const minIdx = typeof d.minIdx === 'number' ? d.minIdx : 0;
      const maxIdx = typeof d.maxIdx === 'number' ? d.maxIdx : (totalSlides - 1);
      const steps = Math.max(1, (maxIdx - minIdx + 1));
      const atStart = currentSlide <= minIdx;
      const atEnd   = currentSlide >= maxIdx;
      if (this.dataset.showProgress === 'true' && this.progressFill && this.slider) {
        
        // Only update if we have valid data
        if (totalSlides > 0) {
          // const progress = ((currentSlide + 1) / totalSlides) * 100;
          // this.progressFill.style.width = `${Math.min(progress, 100)}%`;
          
          // Progress by "page" (works with perView > 1)
          const page = currentSlide - minIdx + 1;
          const progress = Math.min(100, (page / steps) * 100);
          this.progressFill.style.width = `${progress}%`;
        }
      }

      if (this.prevButton) {
        this.prevButton.classList.toggle('ia-visibility-hidden', atStart);
      }

      if (this.nextButton) {
        this.nextButton.classList.toggle('ia-visibility-hidden', atEnd);
      }
    }
  
    next() {
      if (this.slider) {
        this.slider.next();
      }
    }
  
    prev() {
      if (this.slider) {
        this.slider.prev();
      }
    }
  
    goToSlide(index, skipAnimation = false) {
      if (this.slider) {
        if (skipAnimation) {
          this.slider.moveToIdx(index, true, { duration: 0 });
        }
        else {
          this.slider.moveToIdx(index);
        }
      }
    }
  
    createAutoplayPlugin() {
      return (slider) => {
        const clearNextTimeout = () => {
          this.clearAutoplayTimeout();
        };

        const nextTimeout = () => {
          clearNextTimeout();
          if (this.mouseOver) return;
          this.autoplayTimeout = setTimeout(() => {
            if (this.slider) {
              this.slider.next();
            }
          }, this.autoplayDelay);
        };

        slider.on('created', () => {
          this.addEventListener('mouseenter', () => {
            this.mouseOver = true;
            clearNextTimeout();
          });

          this.addEventListener('mouseleave', () => {
            this.mouseOver = false;
            nextTimeout();
          });

          nextTimeout();
        });

        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      };
    }

    clearAutoplayTimeout() {
      if (this.autoplayTimeout) {
        clearTimeout(this.autoplayTimeout);
        this.autoplayTimeout = null;
      }
    }

    destroy() {
      this.clearAutoplayTimeout();
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
    }
  }
  
  
  customElements.define('ia-slider', IASlider);
}